<template>
  <b-container fluid>
    <!-- Title -->
    <b-row class="justify-content-md-center">
      <b-col lg="8">
        <div class="header__title pb-4 pl-3">All messages</div>
      </b-col>
    </b-row>

    <!-- messages -->
    <b-row class="justify-content-md-center mt-4">
      <b-col lg="6" class="text-center">
        <div v-if="hasChannel">
          <template v-for="channel in channels">
            <router-link :key="`channel-${channel.name}`" :to="getDirectMessagePage(channel)">
              <MessageCard
                :channel="channel"
                :user="user"
                :show-last-message="channel.isLearner(user.id)"
              />
            </router-link>
          </template>

          <b-button
            v-show="!loadChannel && $_channelMixin_hasMoreChannels"
            size="sm"
            pill
            variant="outline-primary"
            class="mt-4"
            @click="fetchChannels"
          >
            Load more
          </b-button>
        </div>

        <div v-else>
          <div class="no-message__title">No messages to display</div>

          <div class="no-message__body mt-2">
            All started conversation from learning units will be displaying here
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="loadChannel" class="d-flex justify-content-center">
      <b-spinner class="mt-5" label="Loading..." variant="dark" style="width: 3rem; height: 3rem" />
    </b-row>
  </b-container>
</template>

<script>
import { map } from 'lodash';
import { Channel, Learner, LearningUnit, Section, SendbirdUser, User } from '../../models';
import MessageCard from './components/MessageCard.vue';
import { channelMixin } from '../../mixins';

export default {
  components: {
    MessageCard
  },
  mixins: [channelMixin],

  data() {
    return {
      channelUrls: [],
      loadChannel: false,
      hasChannel: true
    };
  },

  computed: {
    user() {
      return User.query().first();
    },

    channels() {
      return Channel.findChannelIn(this.channelUrls).sort((ch1, ch2) => {
        if (ch2.lastMessage && ch1.lastMessage) {
          return ch2.lastMessage.createdAt - ch1.lastMessage.createdAt;
        } else {
          return 0;
        }
      });
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    }

    // $_channelMixin_isAuthConnected: {
    //   handler: "triggerSendbird",
    // },
  },

  beforeDestroy() {
    Channel.removeEventHandler(`allMessageListener`);
  },

  methods: {
    getDirectMessagePage(channel) {
      const {
        learner: learnerId,
        learning_unit: learningUnitId,
        section: sectionId
      } = channel.metadata;

      return {
        name: 'learning-unit/section/direct-message',
        params: {
          learningUnitId,
          sectionId,
          learnerId
        }
      };
    },

    async onRouteChanged() {
      await this.triggerSendbird();
    },

    async triggerSendbird() {
      const { $_channelMixin_isAuthConnected: isConnected } = this;

      if (isConnected && !this.channelMixin_channelListQuery) {
        const channels = await this.fetchChannels();

        this.hasChannel = channels && channels.length > 0;

        Channel.createEventHandlers(`allMessageListener`, {
          onMessageReceived: async (channel) => {
            const metaData = await channel.getMetaData([
              'learning_unit',
              'section',
              'learner',
              'teachers',
              `${this.user.id}`
            ]);

            if (
              metaData[this.user.id] &&
              channel &&
              channel.url &&
              !this.channelUrls.indexOf(channel.url) !== -1
            ) {
              await this.loadInformation(channel);

              this.channelUrls.push(channel.url);

              this.hasChannel = true;
            }
          }
        });
      }
    },

    async fetchChannels() {
      let channels = [];

      const sendBirdUser = SendbirdUser.find(this.user.id);

      if (sendBirdUser) {
        this.loadChannel = true;

        try {
          channels = await this.$_channelMixin_loadUserChannels(sendBirdUser.id, { limit: 20 });

          if (channels && channels.length > 0) {
            this.channelUrls = [...this.channelUrls, ...map(channels, 'url')];
          }
        } catch (error) {
          console.error(error);
          this.$toast.error('Cannot get direct messages, please try again');
        } finally {
          this.loadChannel = false;
        }
      }

      return channels;
    },

    async loadInformation({ metadata = {} } = {}) {
      let promise;

      if (metadata) {
        const { learning_unit, section, learner } = metadata;

        promise = await Promise.all([
          LearningUnit.api().fetch({ id: learning_unit }),
          Section.api().fetch({ id: section }),
          Learner.api().fetch({ id: learner })
        ]);
      }

      return promise;
    }
  }
};
</script>

<style lang="scss" scoped>
.header__title {
  font-size: 27.2px;
  font-weight: bold;
  border-bottom: 3px solid #f4f5fd;
}

.no-message__title {
  font-size: 19px;
  font-weight: bold;
}

.no-message__body {
  font-size: 15.2px;
  color: rgba(122, 123, 151, 1);
}
</style>
